import React, { useState, useRef, useEffect } from 'react';
import {
  TextField,
  InputAdornment,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Checkbox,
  Paper,
  Divider,
  Chip,
  Avatar
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ErrorIcon from '@mui/icons-material/Error';
import palette from 'src/theme/palette';

const ProjectManagerDropdown = ({
  label,
  name,
  value = [],
  onChange,
  onBlur,
  error,
  helperText,
  placeholder = 'Select options',
  backgroundColor,
  disabled,
  marginBottom,
  height,
  fontSize,
  color,
  labelColor,
  width,
  options = [],
  showNameTag,
  labelFontMedium
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const filteredOptions = options.filter(
    (option) =>
      option.name?.toLowerCase().includes(searchTerm?.toLowerCase()) ||
      option.code?.toLowerCase().includes(searchTerm?.toLowerCase())
  );

  const handleToggle = (optionValue) => {
    const currentIndex = value.indexOf(optionValue);
    const newChecked = [...value];

    if (currentIndex === -1) {
      newChecked.push(optionValue);
    } else {
      newChecked.splice(currentIndex, 1);
    }

    if (onChange) {
      onChange({
        target: {
          name: name,
          value: newChecked
        }
      });
    }
  };

  const handleSelectAll = () => {
    if (value.length === filteredOptions.length) {
      // If all filtered are selected, deselect all
      const newValue = value.filter(
        (val) => !filteredOptions.some((option) => option.value === val)
      );
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: newValue
          }
        });
      }
    } else {
      // Select all filtered options
      const allFilteredValues = filteredOptions.map((option) => option.value);
      if (onChange) {
        onChange({
          target: {
            name: name,
            value: [...new Set([...value, ...allFilteredValues])]
          }
        });
      }
    }
  };

  function stringToColor(str) {
    // Simple hash to color
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${hash % 360}, 75%, 65%)`;
    return color;
  }

  function stringToColorText(str) {
    // Simple hash to color
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const color = `hsl(${hash % 360}, 25%, 35%)`;
    return color;
  }

  const removeChip = (optionValue, e) => {
    e.stopPropagation();
    handleToggle(optionValue);
  };

  return (
    <div
      ref={dropdownRef}
      style={{
        marginBottom: error ? '0.2rem' : marginBottom || '1rem',
        position: 'relative',
        width: width || '100%'
      }}
    >
      {/* Top Label */}
      {label && (
        <Typography
          sx={{
            fontSize: 12,
            marginBottom: '4px',
            color: labelColor ? labelColor : '#1a1a1a'
          }}
        >
          {label}
        </Typography>
      )}

      {/* Custom Dropdown Trigger */}
      <Box
        onClick={() => !disabled && setIsOpen(!isOpen)}
        sx={{
          border: `1px solid ${error ? '#FCA5A5' : '#dcdcdcff'}`,
          borderRadius: '10px',
          padding: '4px 14px',
          minHeight: height ? height : 50,
          backgroundColor: backgroundColor ? backgroundColor : 'transparent',
          cursor: disabled ? 'not-allowed' : 'pointer',
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 0.5,
          transition: 'border-color 0.2s ease-in-out',

          '&:hover': {
            borderColor: error ? '#FCA5A5' : '#010f58'
          },

          '&:focus-within': {
            borderColor: error ? '#FCA5A5' : palette.primary.inputborderfocus,
            boxShadow: error
              ? '0 0 0 2px rgba(211, 47, 47, 0.2)'
              : '0 0 0 2px rgba(25, 118, 210, 0.2)'
          }
        }}
      >
        {value.length === 0 ? (
          <Typography
            sx={{
              fontSize: 12,
              color: '#9e9e9e'
            }}
          >
            {placeholder}
          </Typography>
        ) : (
          value.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return option ? (
              <Chip
                key={val}
                label={option.name}
                size="small"
                onDelete={(e) => removeChip(val, e)}
                sx={{
                  height: 24,
                  fontSize: 11,
                  backgroundColor: palette.primary.chipBackground,
                  '& .MuiChip-deleteIcon': {
                    fontSize: 16
                  }
                }}
              />
            ) : null;
          })
        )}

        <InputAdornment position="end" sx={{ ml: 'auto' }}>
          {error ? (
            <ErrorIcon color="error" sx={{ fontSize: 20 }} />
          ) : (
            <ExpandMoreIcon sx={{ fontSize: 20, color: '#646464' }} />
          )}
        </InputAdornment>
      </Box>

      {/* Error message */}
      {error && (
        <Typography
          sx={{
            fontSize: 11,
            marginLeft: '0px',
            marginTop: '4px',
            color: '#7F1D1D'
          }}
        >
          {helperText}
        </Typography>
      )}

      {/* Dropdown Menu */}
      {isOpen && (
        <Paper
          elevation={3}
          sx={{
            position: 'absolute',
            top: '100%',
            left: 0,
            right: 0,
            zIndex: 1000000,
            mt: 0.5,
            maxHeight: 300,
            overflow: 'auto',
            border: `1px solid #E5E7EB`,
            borderRadius: '6px'
          }}
        >
          {/* Search Input */}
          <Box sx={{ p: 1 }}>
            <TextField
              fullWidth
              autoFocus
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              size="small"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  fontSize: 12,
                  height: 32
                }
              }}
            />
          </Box>

          <Divider />

          {/* Select All Option */}
          {filteredOptions.length > 0 && (
            <>
              <ListItem button onClick={handleSelectAll} sx={{ py: 0, px: 0 }}>
                <Checkbox
                  size="small"
                  checked={
                    value.length > 0 && filteredOptions.every((opt) => value.includes(opt.value))
                  }
                  indeterminate={
                    value.length > 0 &&
                    filteredOptions.some((opt) => value.includes(opt.value)) &&
                    !filteredOptions.every((opt) => value.includes(opt.value))
                  }
                />
                <ListItemText
                  primary="Check All"
                  sx={{
                    '& .MuiTypography-root': {
                      fontSize: 12
                    }
                  }}
                />
              </ListItem>
              <Divider />
            </>
          )}

          {/* Options List */}
          <List sx={{ py: 0, maxHeight: 200, overflow: 'auto' }}>
            {filteredOptions.length > 0 ? (
              filteredOptions.map((option) => {
                return (
                  <ListItem
                    key={option.value}
                    button
                    onClick={() => handleToggle(option.value)}
                    sx={{ py: 0, px: 0 }}
                  >
                    <Checkbox size="small" checked={value.indexOf(option.value) !== -1} />
                    <ListItemText
                      primary={
                        <Box>
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 12,
                              textTransform: 'capitalize'
                            }}
                          >
                            {option.name}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                );
              })
            ) : (
              <ListItem>
                <ListItemText
                  primary="No options found"
                  sx={{
                    textAlign: 'center',
                    '& .MuiTypography-root': {
                      fontSize: 12,
                      color: '#9CA3AF'
                    }
                  }}
                />
              </ListItem>
            )}
          </List>
        </Paper>
      )}
    </div>
  );
};

export default ProjectManagerDropdown;
